// Takvim görünümü (ay / hafta / gün / ajanda) — Blazor karşılığı:
// Octo.Web.Server/Components/Widgets/CalendarWidget.razor (DxScheduler).
// DevExpress Scheduler yerine CSS grid ile çizilir: paket yükü yok, token tabanlı tema tam kontrol.
// Sürükle-bırak yoktur (erişilebilirlik + basitlik); etkinlik tıklanınca pencere açılır.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, ChevronLeft, ChevronRight, Layers, RefreshCw } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { IconButton } from '@/shared/ui/IconButton';
import { SegmentedControl } from '@/shared/ui/SegmentedControl';
import { Spinner } from '@/shared/ui/Spinner';
import { useCalendar } from '../hooks/useCalendar';
import {
  addDays,
  buildMonthGrid,
  buildTimeSlots,
  buildWeekDays,
  dateKey,
  expandEventsByDay,
  formatEventTime,
  formatHourMinute,
  groupEventsByDayList,
  isSameDay,
  layoutDayEvents,
  rangeLabel,
  shiftAnchor,
  splitDayEvents,
  startOfDay,
  weekdayLabels,
  weekdayName,
} from '../model/calendarModel';
import type { CalendarEvent, CalendarViewKind } from '../model/calendarTypes';
import { AddCalendarSourceDialog } from './AddCalendarSourceDialog';
import { CalendarEventDialog, type CalendarEventDraft } from './CalendarEventDialog';
import { CalendarSourceSidebar } from './CalendarSourceSidebar';

export interface CalendarViewProps {
  className?: string;
  /** Masaüstü varsayılan görünümü (mobilde her zaman ajanda ile açılır). */
  defaultView?: CalendarViewKind;
  /** Kaynak paneli düğmesi ve paneli gösterilsin mi. */
  showSidebar?: boolean;
  /** Hafta/gün görünümünün saat penceresi. */
  workStartHour?: number;
  workEndHour?: number;
  agendaDays?: number;
  initialDate?: Date;
}

const HOUR_HEIGHT = 48;

export function CalendarView({
  className,
  defaultView = 'month',
  showSidebar = true,
  workStartHour = 0,
  workEndHour = 24,
  agendaDays = 30,
  initialDate,
}: CalendarViewProps) {
  const { t, i18n } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [anchor, setAnchor] = useState(() => startOfDay(initialDate ?? new Date()));
  const [viewOverride, setViewOverride] = useState<CalendarViewKind | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<CalendarEvent | null>(null);
  const [draft, setDraft] = useState<CalendarEventDraft | null>(null);

  // Mobilde ajanda varsayılan; kullanıcı seçim yaptıysa seçim kazanır (render sırasında türetilir).
  const view: CalendarViewKind = viewOverride ?? (isMobile ? 'agenda' : defaultView);
  const locale = i18n.language || 'tr-TR';

  const calendar = useCalendar({ anchor, view, agendaDays });
  const { sources, visibleEvents, isLoading, isFetching, error } = calendar;

  const openEvent = (event: CalendarEvent) => {
    setDraft(null);
    setSelected(event);
  };
  const openDraft = (start: Date, allDay: boolean) => {
    setSelected(null);
    setDraft({ start, end: new Date(start.getTime() + (allDay ? 0 : 60 * 60_000)), allDay });
  };
  const closeEventDialog = () => {
    setSelected(null);
    setDraft(null);
  };

  const viewOptions = [
    { value: 'month' as const, label: t('calendar.view.month') },
    { value: 'week' as const, label: t('calendar.view.week') },
    { value: 'day' as const, label: t('calendar.view.day') },
    { value: 'agenda' as const, label: t('calendar.view.agenda') },
  ];

  const sidebar = showSidebar ? (
    <CalendarSourceSidebar
      sources={sources}
      onToggle={calendar.toggleSource}
      onDelete={calendar.deleteSource}
      onSync={calendar.syncSource}
      onAdd={() => {
        setSidebarOpen(false);
        setAddOpen(true);
      }}
      syncingSourceId={calendar.syncingSourceId}
    />
  ) : null;

  return (
    <div className={cn('bg-bg text-fg flex h-full min-h-0 flex-col', className)}>
      {/* ── Araç çubuğu ── */}
      <div className="border-border flex flex-wrap items-center gap-1.5 border-b px-2 py-1.5">
        {showSidebar ? (
          <IconButton label={t('calendar.sources.title')} onClick={() => setSidebarOpen((v) => !v)}>
            <Layers className="h-4 w-4" />
          </IconButton>
        ) : null}
        <Button variant="secondary" size="sm" onClick={() => setAnchor(startOfDay(new Date()))}>
          {t('calendar.today')}
        </Button>
        <IconButton
          label={t('calendar.previous')}
          onClick={() => setAnchor((a) => shiftAnchor(a, view, -1, { agendaDays }))}
        >
          <ChevronLeft className="h-4 w-4" />
        </IconButton>
        <IconButton
          label={t('calendar.next')}
          onClick={() => setAnchor((a) => shiftAnchor(a, view, 1, { agendaDays }))}
        >
          <ChevronRight className="h-4 w-4" />
        </IconButton>
        <span className="text-fg min-w-0 flex-1 truncate px-1 text-sm font-semibold">
          {rangeLabel(anchor, view, locale, { agendaDays })}
        </span>
        {isFetching ? <Spinner className="text-fg-subtle h-4 w-4" /> : null}
        <IconButton label={t('calendar.newEvent')} onClick={() => openDraft(nextHour(anchor), false)}>
          <CalendarPlus className="h-4 w-4" />
        </IconButton>
        <IconButton label={t('calendar.refresh')} onClick={calendar.refresh}>
          <RefreshCw className="h-4 w-4" />
        </IconButton>
        <SegmentedControl
          value={view}
          options={viewOptions}
          onChange={setViewOverride}
          ariaLabel={t('calendar.viewSwitch')}
          className="hidden sm:inline-flex"
        />
      </div>

      {/* ── Gövde ── */}
      <div className="flex min-h-0 flex-1">
        {showSidebar && sidebarOpen && !isMobile ? (
          <div className="border-border bg-bg-elevated w-60 shrink-0 border-r">{sidebar}</div>
        ) : null}

        <div className="min-w-0 flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : error ? (
            <EmptyState
              title={t('calendar.loadFailed')}
              description={String((error as { message?: string }).message ?? '')}
            />
          ) : view === 'month' ? (
            <MonthView
              anchor={anchor}
              events={visibleEvents}
              locale={locale}
              onEvent={openEvent}
              onDay={(d) => {
                setAnchor(d);
                setViewOverride('day');
              }}
            />
          ) : view === 'agenda' ? (
            <AgendaView
              anchor={anchor}
              agendaDays={agendaDays}
              events={visibleEvents}
              locale={locale}
              onEvent={openEvent}
            />
          ) : (
            <TimeGridView
              anchor={anchor}
              days={view === 'week' ? buildWeekDays(anchor) : [startOfDay(anchor)]}
              events={visibleEvents}
              locale={locale}
              startHour={workStartHour}
              endHour={workEndHour}
              onEvent={openEvent}
              onSlot={(d) => openDraft(d, false)}
            />
          )}
        </div>
      </div>

      {/* Mobilde kaynak paneli alt sayfa olarak açılır. */}
      {showSidebar && isMobile ? (
        <Dialog
          open={sidebarOpen}
          onOpenChange={setSidebarOpen}
          title={t('calendar.sources.title')}
          size="sm"
        >
          <div className="max-h-[60dvh]">{sidebar}</div>
        </Dialog>
      ) : null}

      {showSidebar ? (
        <AddCalendarSourceDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          userId={calendar.userId}
          onCreate={calendar.createSource}
          creating={calendar.isCreatingSource}
          onRefresh={calendar.refresh}
        />
      ) : null}

      <CalendarEventDialog
        open={!!selected || !!draft}
        onOpenChange={(o) => {
          if (!o) closeEventDialog();
        }}
        event={selected}
        draft={draft}
        sources={sources}
        userId={calendar.userId}
        onSave={calendar.saveEvent}
        onDelete={calendar.deleteEvent}
        saving={calendar.isSavingEvent}
      />
    </div>
  );
}

function nextHour(anchor: Date): Date {
  const now = new Date();
  const base = isSameDay(anchor, now) ? now : anchor;
  return new Date(base.getFullYear(), base.getMonth(), base.getDate(), base.getHours() + 1, 0, 0);
}

// ─────────────────────────────── Ay görünümü ───────────────────────────────

function MonthView({
  anchor,
  events,
  locale,
  onEvent,
  onDay,
}: {
  anchor: Date;
  events: CalendarEvent[];
  locale: string;
  onEvent: (e: CalendarEvent) => void;
  onDay: (d: Date) => void;
}) {
  const { t } = useTranslation();
  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const days = useMemo(() => grid.weeks.flat().map((c) => c.date), [grid]);
  const byDay = useMemo(() => expandEventsByDay(events, days), [events, days]);
  const labels = useMemo(() => weekdayLabels(locale), [locale]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-border text-fg-muted grid shrink-0 grid-cols-7 border-b text-center text-[11px] font-medium uppercase">
        {labels.map((l) => (
          <div key={l} className="py-1">
            {l}
          </div>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 auto-rows-fr grid-cols-7">
        {grid.weeks.flat().map((cell) => {
          const list = byDay.get(cell.key) ?? [];
          const shown = list.slice(0, 3);
          return (
            <div
              key={cell.key}
              className={cn(
                'border-border flex min-h-[72px] min-w-0 flex-col gap-0.5 border-r border-b p-1',
                !cell.inMonth && 'bg-bg-muted/50',
                cell.isWeekend && cell.inMonth && 'bg-bg-muted/30',
              )}
            >
              <button
                type="button"
                onClick={() => onDay(cell.date)}
                className={cn(
                  'focus-visible:ring-ring self-start rounded px-1 text-xs focus-visible:ring-2 focus-visible:outline-none',
                  cell.inMonth ? 'text-fg' : 'text-fg-subtle',
                  cell.isToday && 'bg-brand-600 font-semibold text-white',
                )}
                aria-label={cell.key}
              >
                {cell.date.getDate()}
              </button>
              {shown.map((e) => (
                <EventChip key={`${cell.key}-${e.id}`} event={e} onClick={() => onEvent(e)} compact />
              ))}
              {list.length > shown.length ? (
                <button
                  type="button"
                  onClick={() => onDay(cell.date)}
                  className="text-fg-muted hover:text-fg px-1 text-left text-[11px]"
                >
                  {t('calendar.more', { count: list.length - shown.length })}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────── Hafta / gün görünümü ───────────────────────────────

function TimeGridView({
  anchor,
  days,
  events,
  locale,
  startHour,
  endHour,
  onEvent,
  onSlot,
}: {
  anchor: Date;
  days: Date[];
  events: CalendarEvent[];
  locale: string;
  startHour: number;
  endHour: number;
  onEvent: (e: CalendarEvent) => void;
  onSlot: (d: Date) => void;
}) {
  const { t } = useTranslation();
  const slots = useMemo(() => buildTimeSlots(startHour, endHour, 60), [startHour, endHour]);
  const byDay = useMemo(() => expandEventsByDay(events, days), [events, days]);
  const today = startOfDay(new Date());
  const bodyHeight = slots.length * HOUR_HEIGHT;
  const hasAllDay = days.some((d) => splitDayEvents(byDay.get(dateKey(d)) ?? [], d).allDay.length > 0);

  return (
    <div className="min-w-0">
      {/* Gün başlıkları */}
      <div className="border-border bg-bg-elevated sticky top-0 z-10 flex border-b">
        <div className="w-12 shrink-0" />
        {days.map((d) => (
          <div key={dateKey(d)} className="border-border min-w-0 flex-1 border-l px-1 py-1 text-center">
            <div className="text-fg-muted text-[11px] uppercase">{weekdayName(d, locale, 'short')}</div>
            <div className={cn('text-sm font-semibold', isSameDay(d, today) ? 'text-brand-600' : 'text-fg')}>
              {d.getDate()}
            </div>
          </div>
        ))}
      </div>

      {/* Tüm gün şeridi */}
      {hasAllDay ? (
        <div className="border-border flex border-b">
          <div className="text-fg-subtle w-12 shrink-0 px-1 py-1 text-right text-[10px]">
            {t('calendar.allDay')}
          </div>
          {days.map((d) => {
            const { allDay } = splitDayEvents(byDay.get(dateKey(d)) ?? [], d);
            return (
              <div key={dateKey(d)} className="border-border min-w-0 flex-1 space-y-0.5 border-l p-0.5">
                {allDay.map((e) => (
                  <EventChip key={`${dateKey(d)}-${e.id}`} event={e} onClick={() => onEvent(e)} compact />
                ))}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Saat ızgarası */}
      <div className="flex">
        <div className="w-12 shrink-0">
          {slots.map((s) => (
            <div
              key={s.minutes}
              className="text-fg-subtle h-12 pr-1 text-right text-[10px]"
              style={{ height: HOUR_HEIGHT }}
            >
              {s.label}
            </div>
          ))}
        </div>
        {days.map((d) => {
          const { timed } = splitDayEvents(byDay.get(dateKey(d)) ?? [], d);
          const positioned = layoutDayEvents(timed, d, { startHour, endHour });
          return (
            <div
              key={dateKey(d)}
              className="border-border relative min-w-0 flex-1 border-l"
              style={{ height: bodyHeight }}
            >
              {slots.map((s) => (
                <button
                  key={s.minutes}
                  type="button"
                  onClick={() =>
                    onSlot(new Date(d.getFullYear(), d.getMonth(), d.getDate(), s.hour, s.minute))
                  }
                  aria-label={`${dateKey(d)} ${s.label}`}
                  className="border-border hover:bg-bg-hover/60 block w-full border-b"
                  style={{ height: HOUR_HEIGHT }}
                />
              ))}
              {positioned.map((p) => (
                <button
                  key={`${dateKey(d)}-${p.event.id}`}
                  type="button"
                  onClick={() => onEvent(p.event)}
                  className="absolute overflow-hidden rounded px-1 py-0.5 text-left text-[11px] leading-tight text-white shadow-sm"
                  style={{
                    top: `${p.top}%`,
                    height: `${p.height}%`,
                    left: `${(p.column / p.columnCount) * 100}%`,
                    width: `${100 / p.columnCount}%`,
                    backgroundColor: p.event.color,
                  }}
                  title={`${p.event.title} · ${formatEventTime(p.event, t('calendar.allDay'))}`}
                >
                  <span className="block truncate font-medium">{p.event.title}</span>
                  <span className="block truncate opacity-80">{formatHourMinute(p.event.start)}</span>
                </button>
              ))}
              {isSameDay(d, today) ? <NowIndicator startHour={startHour} endHour={endHour} /> : null}
            </div>
          );
        })}
      </div>
      <span className="sr-only">{rangeLabel(anchor, days.length > 1 ? 'week' : 'day', locale)}</span>
    </div>
  );
}

function NowIndicator({ startHour, endHour }: { startHour: number; endHour: number }) {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  const from = startHour * 60;
  const span = Math.max(1, endHour * 60 - from);
  if (minutes < from || minutes > from + span) return null;
  return (
    <div
      className="bg-danger pointer-events-none absolute right-0 left-0 h-px"
      style={{ top: `${((minutes - from) / span) * 100}%` }}
      aria-hidden="true"
    />
  );
}

// ─────────────────────────────── Ajanda görünümü ───────────────────────────────

function AgendaView({
  anchor,
  agendaDays,
  events,
  locale,
  onEvent,
}: {
  anchor: Date;
  agendaDays: number;
  events: CalendarEvent[];
  locale: string;
  onEvent: (e: CalendarEvent) => void;
}) {
  const { t } = useTranslation();
  const range = useMemo(
    () => ({ start: startOfDay(anchor), end: addDays(startOfDay(anchor), agendaDays) }),
    [anchor, agendaDays],
  );
  const groups = useMemo(() => groupEventsByDayList(events, range), [events, range]);
  const today = startOfDay(new Date());

  if (!groups.length)
    return <EmptyState title={t('calendar.noEvents')} description={t('calendar.noEventsHint')} />;

  return (
    <ul className="divide-border divide-y">
      {groups.map((g) => (
        <li key={g.key} className="px-3 py-2">
          <div
            className={cn(
              'mb-1 text-xs font-semibold uppercase',
              isSameDay(g.date, today) ? 'text-brand-600' : 'text-fg-muted',
            )}
          >
            {g.date.getDate()} {weekdayName(g.date, locale, 'short')} · {weekdayName(g.date, locale)}
          </div>
          <ul className="space-y-1">
            {g.events.map((e) => (
              <li key={`${g.key}-${e.id}`}>
                <button
                  type="button"
                  onClick={() => onEvent(e)}
                  className="hover:bg-bg-hover focus-visible:ring-ring flex w-full items-start gap-2 rounded-md px-1.5 py-1 text-left focus-visible:ring-2 focus-visible:outline-none"
                >
                  <span
                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: e.color }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="text-fg block truncate text-sm font-medium">{e.title}</span>
                    <span className="text-fg-muted block truncate text-xs">
                      {formatEventTime(e, t('calendar.allDay'))}
                      {e.location ? ` · ${e.location}` : ''}
                      {e.sourceName ? ` · ${e.sourceName}` : ''}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────────────────── Ortak parça ───────────────────────────────

function EventChip({
  event,
  onClick,
  compact,
}: {
  event: CalendarEvent;
  onClick: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${event.title} · ${formatEventTime(event, t('calendar.allDay'))}`}
      className={cn(
        'hover:bg-bg-hover focus-visible:ring-ring flex w-full min-w-0 items-center gap-1 rounded px-1 text-left focus-visible:ring-2 focus-visible:outline-none',
        compact ? 'text-[11px]' : 'text-xs',
        event.allDay && 'text-white hover:opacity-90',
      )}
      style={event.allDay ? { backgroundColor: event.color } : undefined}
    >
      {event.allDay ? null : (
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: event.color }}
          aria-hidden="true"
        />
      )}
      {event.allDay ? null : <span className="text-fg-muted shrink-0">{formatHourMinute(event.start)}</span>}
      <span className="min-w-0 truncate">{event.title}</span>
    </button>
  );
}
